import { Component, Inject } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';

import {
  CurrentReleaseNote,
  ReleaseNoteCategory,
  ReleaseNotesDialogResult,
} from 'app/models/release-note.model';
import { ReleaseNotesService } from 'app/services/release-notes.service';

export interface ReleaseNotesDialogData {
  note: CurrentReleaseNote;
  /**
   * When false the dialog is read-only (the "What's New" menu path): no
   * checkbox, no dismiss call — just the content.
   */
  allowDismiss?: boolean;
}

/**
 * Opens the release-notes popup.
 *
 * disableClose keeps the backdrop/ESC from silently discarding the choice; the
 * component routes those through close() so the caller always gets a result,
 * matching ManageViewsDialogComponent.
 */
export function openReleaseNotesDialog(
  dialog: MatDialog,
  data: ReleaseNotesDialogData,
): Observable<ReleaseNotesDialogResult | undefined> {
  const config: MatDialogConfig = {
    width: '720px',
    maxHeight: '85vh',
    disableClose: true,
    autoFocus: false,
    panelClass: 'release-notes-dialog-panel',
    data,
  };
  return dialog
    .open<ReleaseNotesDialog, ReleaseNotesDialogData, ReleaseNotesDialogResult>(
      ReleaseNotesDialog,
      config,
    )
    .afterClosed();
}

@Component({
  selector: 'app-release-notes-dialog',
  templateUrl: './release-notes-dialog.html',
  styleUrls: ['./release-notes-dialog.scss'],
  standalone: false,
})
export class ReleaseNotesDialog {
  dontShowAgain = false;
  saving = false;

  readonly note: CurrentReleaseNote;
  readonly allowDismiss: boolean;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: ReleaseNotesDialogData,
    private dialogRef: MatDialogRef<ReleaseNotesDialog, ReleaseNotesDialogResult>,
    private service: ReleaseNotesService,
  ) {
    this.note = data.note;
    this.allowDismiss = data.allowDismiss !== false;

    // disableClose is on, so funnel backdrop/ESC through the same "remind me later" path.
    this.dialogRef.backdropClick().subscribe(() => this.close());
    this.dialogRef.keydownEvents().subscribe((e) => {
      if (e.key === 'Escape') this.close();
    });
  }

  iconFor(category: ReleaseNoteCategory): string {
    switch (category) {
      case 'New':
        return 'auto_awesome';
      case 'Improved':
        return 'trending_up';
      case 'Fixed':
        return 'build';
      default:
        return 'chevron_right';
    }
  }

  /** "Close" / backdrop / ESC — leaves the note outstanding for the next sign-in. */
  close(): void {
    this.persist(false);
  }

  /** "Got it" — honours the checkbox. */
  confirm(): void {
    this.persist(this.dontShowAgain);
  }

  private persist(dontShowAgain: boolean): void {
    const result: ReleaseNotesDialogResult = {
      action: dontShowAgain ? 'dismissed' : 'later',
      dontShowAgain,
    };

    if (!this.allowDismiss || !this.note.releaseNoteID) {
      this.dialogRef.close(result);
      return;
    }

    // Close on the server round-trip, not before it: a failed dismiss must not
    // look like it stuck. (The existing alert dialog closes optimistically and
    // silently reopens next load — deliberately not copying that.)
    this.saving = true;
    this.service.dismiss(this.note.releaseNoteID, dontShowAgain).subscribe({
      next: () => {
        this.saving = false;
        this.dialogRef.close(result);
      },
      error: (err) => {
        console.error('Error dismissing release note:', err);
        this.saving = false;
        // Close anyway — the user should never be trapped by a failed write.
        this.dialogRef.close(result);
      },
    });
  }
}
